import React, { useEffect, useState } from "react";
import { Row, Col, Form, Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faEye,
  faCheck,
  faTimes,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast } from "react-hot-toast";
import moment from "moment";
import { generateConfirm } from "../../helpers";
import useScrollTop from "../../hooks/useScrollTop";
import {
  JobsListing,
  JobsStatus,
  InternListing,
  InternStatus,
} from "../../services/jobs";

const JobsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isInternship = location.pathname.startsWith("/internships-list");

  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Initialize payload with saved state or defaults
  const STORAGE_KEY = isInternship
    ? "internshipListPaginationState"
    : "jobListPaginationState";
  const [payload, setPayload] = useState(() => {
    try {
      const savedState = sessionStorage.getItem(STORAGE_KEY);
      if (savedState) return JSON.parse(savedState);
    } catch (error) {
      console.error("Error loading saved pagination state:", error);
    }
    return { page: 1, limit: 10, search_key: "" };
  });

  const pageCount = Math.ceil(totalCount / payload.limit);
  useScrollTop();

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [payload, STORAGE_KEY]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (
        !window.location.href.includes(isInternship ? "/internships" : "/jobs")
      ) {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isInternship, STORAGE_KEY]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = isInternship
        ? await InternListing(payload)
        : await JobsListing(payload);

      const responseData = response.data;

      if (responseData && responseData.statusCode === 200) {
        setItems(responseData.result);
        setTotalCount(responseData.total_count);
      } else {
        console.error("Invalid response format:", responseData);
        toast.error(
          responseData?.statusMessage ||
            `Failed to fetch ${isInternship ? "internships" : "jobs"}`
        );
      }
    } catch (error) {
      console.error(
        `Error fetching ${isInternship ? "internships" : "jobs"}:`,
        error
      );
      toast.error(`Error fetching ${isInternship ? "internships" : "jobs"}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [payload, isInternship]);

  const handlePageClick = (e) =>
    setPayload({ ...payload, page: e.selected + 1 });

  const handleSearch = () =>
    setPayload({ ...payload, search_key: searchTerm, page: 1 });

  const handleViewItem = (itemId) =>
    navigate(
      isInternship ? `/view-internships/${itemId}` : `/view-jobs/${itemId}`
    );

  const getStatusText = (status) => {
    switch (status) {
      case "0":
        return "pending";
      case "1":
        return "approved";
      case "2":
        return "rejected";
      default:
        return "pending";
    }
  };

  const callUpdateStatusAPI = async (itemId, newStatus, reason = "") => {
    try {
      setIsUpdatingStatus(true);

      const statusPayload = isInternship
        ? {
            internship_id: itemId.toString(),
            internship_status: newStatus.toString(),
            ...(newStatus === "2" && { reject_reason: reason }),
          }
        : {
            job_id: itemId.toString(),
            job_status: newStatus.toString(),
            ...(newStatus === "2" && { reject_reason: reason }),
          };

      const response = isInternship
        ? await InternStatus(statusPayload)
        : await JobsStatus(statusPayload);

      const responseData = response.data;

      if (responseData && responseData.statusCode === 200) {
        setItems(
          items.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  [isInternship ? "internship_status" : "job_status"]:
                    newStatus.toString(),
                  ...(newStatus === "2" && { reject_reason: reason }),
                }
              : item
          )
        );
        toast.success(
          responseData.statusMessage ||
            `${isInternship ? "Internship" : "Job"} ${getStatusText(
              newStatus
            )} successfully`
        );
      } else {
        toast.error(
          responseData?.statusMessage ||
            `Failed to update ${isInternship ? "internship" : "job"} status`
        );
      }
    } catch (error) {
      console.error(
        `Error updating ${isInternship ? "internship" : "job"} status:`,
        error
      );
      toast.error(
        `Error updating ${isInternship ? "internship" : "job"} status`
      );
    } finally {
      setIsUpdatingStatus(false);
      setShowRejectModal(false);
      setRejectReason("");
      setSelectedItemId(null);
    }
  };

  const handleApprove = (itemId) =>
    generateConfirm(
      `Approve this ${isInternship ? "internship" : "job"}?`,
      `Press Yes to approve this ${isInternship ? "internship" : "job"}.`,
      `${isInternship ? "internship" : "job"}-approve`,
      () => callUpdateStatusAPI(itemId, "1")
    );

  const handleReject = (itemId) => {
    setSelectedItemId(itemId);
    setShowRejectModal(true);
  };

  const handleRejectConfirm = () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    callUpdateStatusAPI(selectedItemId, "2", rejectReason);
  };

  const formatDateTime = (dateString) =>
    moment(dateString).format("YYYY-MM-DD hh:mm A");

  const tableHeaders = [
    "S.No",
    "Company",
    isInternship ? "Internship Position" : "Position",
    "Type",
    "Location",
    "Experience",
    "Posted",
    "Status",
    "Actions",
  ];

  return (
    <div className="dashboard review-list">
      <div className="subheader">
        <Row>
          <Col xs={5}>
            <h2 className="heading-36">
              {isInternship ? "Internships List" : "Jobs List"}
            </h2>
          </Col>
          <Col xs={7}>
            <div className="subheader-right">
              <Form.Group controlId="formFile" className="subheader-search">
                <Form.Control
                  type="search"
                  placeholder={`Search by company name or ${
                    isInternship ? "position" : "job title"
                  }`}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  value={searchTerm}
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="search-icon"
                  onClick={handleSearch}
                />
              </Form.Group>
            </div>
          </Col>
        </Row>
      </div>

      <div className="table-main fixTableHead">
        {!isLoading ? (
          items.length > 0 ? (
            <table className="table nowrap table-bordered table-default-dark">
              <thead>
                <tr className="table-heading">
                  {tableHeaders.map((header, index) => (
                    <th key={index}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const status = isInternship
                    ? item.internship_status
                    : item.job_status;
                  return (
                    <tr key={item.id}>
                      <td>{(payload.page - 1) * payload.limit + index + 1}</td>
                      <td>
                        <span
                          className="truncate-title"
                          title={item.companyName || "N/A"}
                        >
                          {item.companyName?.substring(0, 20) || "N/A"}
                          {item.companyName?.length > 20 && "..."}
                        </span>
                      </td>
                      <td
                        className="truncate-title"
                        title={
                          isInternship
                            ? item.internshipPositions
                            : item.jobPositions || "N/A"
                        }
                      >
                        {(isInternship
                          ? item.internshipPositions
                          : item.jobPositions
                        )?.substring(0, 20) || "N/A"}
                        {(isInternship
                          ? item.internshipPositions
                          : item.jobPositions
                        )?.length > 20 && "..."}
                      </td>
                      <td>
                        {isInternship
                          ? item.typesOfInternship
                          : item.typesOfJob}
                      </td>
                      <td>
                        {isInternship
                          ? item.internshipLocation
                          : item.jobLocation}
                      </td>
                      <td>{item.experienceRequired}</td>
                      <td>{formatDateTime(item.createdAt)}</td>
                      <td>{getStatusText(status)}</td>
                      <td>
                        <div className="action-buttons">
                          <Button
                            className="action-btn view-btn"
                            onClick={() => handleViewItem(item.id)}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                          <Button
                            className="action-btn approve-btn"
                            onClick={() => handleApprove(item.id)}
                            disabled={status === "1" || isUpdatingStatus}
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </Button>
                          <Button
                            className="action-btn reject-btn"
                            onClick={() => handleReject(item.id)}
                            disabled={status === "2" || isUpdatingStatus}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <h1 className="no-data">
              No {isInternship ? "Internships" : "Jobs"} Found
            </h1>
          )
        ) : (
          <h1 className="no-data">Loading...</h1>
        )}
      </div>

      {totalCount > payload.limit && (
        <ReactPaginate
          previousLabel={<FontAwesomeIcon icon={faAngleDoubleLeft} />}
          nextLabel={<FontAwesomeIcon icon={faAngleDoubleRight} />}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={handlePageClick}
          containerClassName={"pagination custom-pagination"}
          activeClassName={"active"}
          forcePage={payload.page - 1}
        />
      )}

      <Modal
        show={showRejectModal}
        onHide={() => setShowRejectModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Reject {isInternship ? "Internship" : "Job"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="rejectReason">
            <Form.Label>
              Please provide a reason for rejection (max 200 characters)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Enter rejection reason"
              value={rejectReason}
              onChange={(e) => {
                if (e.target.value.length <= 200) {
                  setRejectReason(e.target.value);
                }
              }}
              maxLength={200}
            />
            <Form.Text className="text-muted">
              {rejectReason.length}/200 characters
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowRejectModal(false)}
            disabled={isUpdatingStatus}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRejectConfirm}
            disabled={isUpdatingStatus}
          >
            {isUpdatingStatus
              ? "Rejecting..."
              : `Reject ${isInternship ? "Internship" : "Job"}`}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default JobsList;
